import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api/axios'
import { useToast } from 'vue-toastification'

export const useAlertStore = defineStore('alerts', () => {
  const toast = useToast()
  
  // State
  const alerts = ref([])
  const loading = ref(false)
  const error = ref(null)
  const filters = ref({
    status: '',
    severity: '',
    type: '',
    profileId: null
  })
  
  // Getters
  const activeAlerts = computed(() => 
    alerts.value.filter(a => a.status === 'active')
  )
  
  const criticalAlerts = computed(() =>
    alerts.value.filter(a => a.severity === 'critical' && a.status === 'active')
  )
  
  const alertsByProfile = computed(() => (profileId) => 
    alerts.value.filter(a => a.profile_id === parseInt(profileId))
  )
  
  const alertsBySeverity = computed(() => {
    const grouped = {
      critical: [],
      high: [],
      medium: [],
      low: []
    }
    
    alerts.value.forEach(alert => {
      if (grouped[alert.severity]) {
        grouped[alert.severity].push(alert)
      }
    })
    
    return grouped
  })
  
  const alertStats = computed(() => ({
    total: alerts.value.length,
    active: activeAlerts.value.length,
    critical: criticalAlerts.value.length,
    acknowledged: alerts.value.filter(a => a.status === 'acknowledged').length,
    resolved: alerts.value.filter(a => a.status === 'resolved').length
  }))
  
  // Actions
  async function fetchAlerts(params = {}) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get('/alerts/', { params })
      alerts.value = response.data.alerts
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to fetch alerts'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function fetchActiveAlerts() {
    return fetchAlerts({ status: 'active' })
  }
  
  async function acknowledgeAlert(id) {
    try {
      const response = await api.post(`/alerts/${id}/acknowledge`)
      
      const index = alerts.value.findIndex(a => a.id === parseInt(id))
      if (index !== -1) {
        alerts.value[index] = response.data.alert
      }
      
      toast.success('Alert acknowledged')
      return response.data.alert
    } catch (err) {
      toast.error('Failed to acknowledge alert')
      throw err
    }
  }
  
  async function resolveAlert(id, resolution) {
    try {
      const response = await api.post(`/alerts/${id}/resolve`, { resolution })
      
      const index = alerts.value.findIndex(a => a.id === parseInt(id))
      if (index !== -1) {
        alerts.value[index] = response.data.alert
      }
      
      toast.success('Alert resolved')
      return response.data.alert
    } catch (err) {
      toast.error('Failed to resolve alert')
      throw err
    }
  }
  
  async function dismissAlert(id) {
    try {
      const response = await api.post(`/alerts/${id}/dismiss`)
      
      const index = alerts.value.findIndex(a => a.id === parseInt(id))
      if (index !== -1) {
        alerts.value[index] = response.data.alert
      }
      
      toast.info('Alert dismissed')
      return response.data.alert
    } catch (err) {
      toast.error('Failed to dismiss alert')
      throw err
    }
  }
  
  async function bulkAcknowledge(alertIds) {
    try {
      const response = await api.post('/alerts/bulk-acknowledge', { alert_ids: alertIds })
      
      response.data.alerts.forEach(updatedAlert => {
        const index = alerts.value.findIndex(a => a.id === updatedAlert.id)
        if (index !== -1) {
          alerts.value[index] = updatedAlert
        }
      })
      
      toast.success(`${alertIds.length} alerts acknowledged`)
      return response.data.alerts
    } catch (err) {
      toast.error('Failed to acknowledge alerts')
      throw err
    }
  }
  
  async function updateAlertSettings(settings) {
    try {
      const response = await api.put('/alerts/settings', settings)
      toast.success('Alert settings updated')
      return response.data
    } catch (err) {
      toast.error('Failed to update alert settings')
      throw err
    }
  }
  
  return {
    // State
    alerts,
    loading,
    error,
    filters,
    
    // Getters
    activeAlerts,
    criticalAlerts,
    alertsByProfile,
    alertsBySeverity,
    alertStats,
    
    // Actions
    fetchAlerts,
    fetchActiveAlerts,
    acknowledgeAlert,
    resolveAlert,
    dismissAlert,
    bulkAcknowledge,
    updateAlertSettings
  }
})
